"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { HEALTH_AI_CONSENT_VERSION } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/onboarding/Checkbox"
import { useI18n } from "@/i18n"
import { useAuth } from "@/hooks/useAuth"
import { PRIVACY_DATA_PATH, clearHealthConsentRequired } from "@/lib/healthConsentGate"
import { LEGAL_PATHS } from "@/lib/legalRoutes"

interface HealthConsentScreenProps {
  /** Called after the server has recorded the consent. */
  onConsented: () => void
}

/**
 * Explicit consent to process health information with the third-party AI (Apple guideline 5.1.2(i),
 * GDPR Art. 9(2)(a)). One screen, one unticked checkbox, one button: "Weiter" stays disabled until the
 * box is ticked, then POST /v1/me/health-ai-consent at the current wording version. Shown as the onboarding
 * interstitial (before the first health step) and as the blocking gate for existing accounts / any 403
 * HEALTH_AI_CONSENT_REQUIRED. There is deliberately no "decline and continue" path: without consent the
 * server refuses every health-data write, plan and Amy turn (fail closed), so the screen says so. A person
 * who does not agree is not trapped though: a quiet secondary block under the button leads to Privacy & data
 * (export, delete — both work without consent, GDPR Art. 15/17/20) and offers sign-out.
 *
 * Layout mirrors the onboarding step shell (eyebrow, h1, body, controls, footer button) without being one:
 * the shell's Continue button is only aria-disabled (it shows a "required field" callout); here the button
 * is genuinely disabled until the box is ticked.
 */
export function HealthConsentScreen({ onConsented }: HealthConsentScreenProps) {
  const { t } = useI18n()
  const api = useApiClient()
  const { signOut } = useAuth()
  const [checked, setChecked] = useState(false)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)

  // Focus order: heading (focused on mount) -> body -> checkbox -> privacy link -> button.
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  async function submit() {
    if (!checked || saving) return
    setSaving(true)
    setFailed(false)
    try {
      await api.postHealthAiConsent({ version: HEALTH_AI_CONSENT_VERSION })
    } catch {
      setFailed(true)
      setSaving(false)
      return
    }
    clearHealthConsentRequired()
    onConsented()
  }

  return (
    <section
      aria-labelledby="health-consent-title"
      className="mx-auto flex w-full max-w-2xl flex-col gap-6"
    >
      <div>
        <p className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t("healthConsent.eyebrow")}
        </p>
        <h1
          id="health-consent-title"
          ref={headingRef}
          tabIndex={-1}
          className="font-display mt-1 text-3xl font-bold tracking-tight text-ink outline-none"
        >
          {t("healthConsent.title")}
        </h1>
        <p className="mt-2 text-sm text-ink-2">{t("healthConsent.body")}</p>
      </div>

      <label
        htmlFor="health-consent-checkbox"
        className="flex min-h-11 cursor-pointer items-start gap-3 rounded-bb-md border border-line bg-paper p-4"
      >
        <Checkbox id="health-consent-checkbox" checked={checked} onCheckedChange={setChecked} />
        <span className="text-sm font-medium text-ink">{t("healthConsent.checkbox")}</span>
      </label>

      <p className="text-sm text-ink-2">
        {t("healthConsent.footnote")}{" "}
        <a
          href={LEGAL_PATHS.privacy}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center font-semibold text-ink underline underline-offset-2 hover:text-green"
        >
          {t("healthConsent.privacyLink")}
        </a>
      </p>

      {failed && (
        <p role="alert" className="text-sm text-destructive">
          {t("healthConsent.saveError")}
        </p>
      )}

      <footer className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          className="min-h-12 w-full rounded-bb-full sm:w-auto sm:min-w-44"
          disabled={!checked || saving}
          onClick={() => void submit()}
        >
          {saving ? t("healthConsent.saving") : t("healthConsent.continue")}
        </Button>
      </footer>

      {/* A person who does not agree can still reach their data and leave: export / delete live on Privacy & data
          (exempt from the gate), and sign-out is the same path the app's nav uses. Clearly secondary to the
          button above. */}
      <div className="flex flex-col gap-1 border-t border-line pt-4 text-xs text-ink-2">
        <p>{t("healthConsent.declineHint")}</p>
        <div className="flex flex-wrap items-center gap-x-5">
          <Link
            to={PRIVACY_DATA_PATH}
            className="inline-flex min-h-11 items-center font-semibold underline underline-offset-2 hover:text-ink"
          >
            {t("privacy.title")}
          </Link>
          <button
            type="button"
            onClick={() => void signOut()}
            className="inline-flex min-h-11 items-center font-semibold underline underline-offset-2 hover:text-ink"
          >
            {t("nav.signOut")}
          </button>
        </div>
      </div>
    </section>
  )
}
